import { Button, Modal, Textarea } from '@ohear/ui'
/**
 * Frame.io-style click-to-comment overlay rendered over the video preview.
 *
 * PT styling: pins are circular bevel chips (white border + role color fill),
 * comment counter is a PT chrome chip pinned to the bottom-right of the
 * video, modals use the standard PT-light panel.
 *
 * The overlay does not own the video element — the parent passes a ref so we
 * can read the current playback timestamp at click time.
 */
import type { RefObject } from 'react'
import { useState } from 'react'
import {
  type FrameComment,
  usePostFrameComment,
  useProjectComments,
  useReplyFrameComment,
  useResolveFrameComment,
} from '../../lib/api/comments'

export interface FrameCommentsOverlayProps {
  projectId: string
  /** Ref to the video element so we can read currentTime at click time. */
  videoRef: RefObject<HTMLVideoElement>
  /** Container the pins position against. */
  containerRef: RefObject<HTMLElement>
  /** Active cue id — when present, frame comments anchor to the cue
   *  (target_type='cue') rather than the project. */
  activeCueId?: string | null
  /** Active cue in-time in seconds. Used as the default timecode when the
   *  reference video has no src loaded (so currentTime is always 0). */
  activeCueInSec?: number | null
}

interface PinStyle {
  bg: string
  border: string
}

// Status palette: open vs resolved drives base color, role drives accent.
// Saturated PT pastels for open pins, neutral grey for resolved.
const PIN_STYLES: Record<'open' | 'resolved', Record<FrameComment['author_role'], PinStyle>> = {
  open: {
    producer: { bg: '#ffaa1d', border: '#ffffff' },
    supervisor: { bg: '#16a34a', border: '#ffffff' },
    actor: { bg: '#2563eb', border: '#ffffff' },
    owner: { bg: '#a855f7', border: '#ffffff' },
    member: { bg: '#5a5a5e', border: '#ffffff' },
  },
  resolved: {
    producer: { bg: '#9c9ca2', border: '#ffffff' },
    supervisor: { bg: '#9c9ca2', border: '#ffffff' },
    actor: { bg: '#9c9ca2', border: '#ffffff' },
    owner: { bg: '#9c9ca2', border: '#ffffff' },
    member: { bg: '#9c9ca2', border: '#ffffff' },
  },
}

function pinStyleFor(c: FrameComment): PinStyle {
  const status = c.resolved_for_me ? 'resolved' : 'open'
  return PIN_STYLES[status][c.author_role] ?? PIN_STYLES[status].member
}

export function FrameCommentsOverlay({
  projectId,
  videoRef,
  containerRef,
  activeCueId,
  activeCueInSec,
}: FrameCommentsOverlayProps) {
  const { data } = useProjectComments(projectId)
  const post = usePostFrameComment(projectId)
  const reply = useReplyFrameComment(projectId)
  const resolve = useResolveFrameComment(projectId)

  const [composeOpen, setComposeOpen] = useState(false)
  const [composeAnchor, setComposeAnchor] = useState<{ tc: number; x: number; y: number } | null>(
    null,
  )
  const [composeBody, setComposeBody] = useState('')

  const [threadOpen, setThreadOpen] = useState<FrameComment | null>(null)
  const [replyBody, setReplyBody] = useState('')

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-frame-pin]')) return
    const container = containerRef.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    // Prefer the live video position; fall back to the active cue's in-time
    // when the video has no src loaded (currentTime is always 0 in that
    // case, which would silently bucket every frame comment at t=0).
    const video = videoRef.current
    const liveTc =
      video && Number.isFinite(video.currentTime) && video.currentTime > 0
        ? video.currentTime
        : null
    const tc = liveTc ?? activeCueInSec ?? 0
    setComposeAnchor({ tc, x, y })
    setComposeBody('')
    setComposeOpen(true)
  }

  const handleSubmit = async () => {
    if (!composeAnchor || composeBody.trim().length === 0) return
    await post.mutateAsync({
      timecodeSec: composeAnchor.tc,
      xPct: composeAnchor.x,
      yPct: composeAnchor.y,
      body: composeBody.trim(),
      cueId: activeCueId ?? undefined,
    })
    setComposeOpen(false)
    setComposeAnchor(null)
    setComposeBody('')
  }

  const handleReply = async () => {
    if (!threadOpen || replyBody.trim().length === 0) return
    await reply.mutateAsync({ commentId: threadOpen.comment_id, body: replyBody.trim() })
    setReplyBody('')
  }

  const comments = data?.comments ?? []
  const openCount = comments.filter((c) => !c.resolved_for_me).length

  return (
    <>
      <div
        aria-label="Click anywhere to add a comment"
        onClick={handleContainerClick}
        className="absolute inset-0 cursor-crosshair"
      >
        {comments.map((c) => {
          const style = pinStyleFor(c)
          const isOpen = !c.resolved_for_me
          return (
            <button
              key={c.comment_id}
              type="button"
              data-frame-pin
              data-pin-status={isOpen ? 'open' : 'resolved'}
              onClick={(e) => {
                e.stopPropagation()
                setThreadOpen(c)
                setReplyBody('')
              }}
              className="absolute flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white transition-transform hover:scale-110"
              style={{
                left: `${c.x_pct * 100}%`,
                top: `${c.y_pct * 100}%`,
                transform: 'translate(-50%, -50%)',
                background: style.bg,
                border: `2px solid ${style.border}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.6)',
                opacity: isOpen ? 1 : 0.65,
              }}
              title={`${c.author_name}: ${c.body.slice(0, 80)}`}
              aria-label={`Comment ${c.pin_number} from ${c.author_name}${isOpen ? ' (unread)' : ' (resolved)'}`}
            >
              {c.pin_number}
            </button>
          )
        })}
        {comments.length > 0 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-2 rounded-sm border border-pt-border bg-pt-panel2 px-3 py-1 text-[11px] text-pt-text shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
            <span aria-hidden="true">💬</span>
            <span className="pt-mono font-semibold">
              <span className="text-pt-text">{openCount}</span> open · {comments.length} total
            </span>
          </div>
        )}
      </div>

      {/* Compose modal */}
      <Modal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        title="Add a comment"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setComposeOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={post.isPending || composeBody.trim().length === 0}
              loading={post.isPending}
            >
              Post comment
            </Button>
          </div>
        }
      >
        {composeAnchor && (
          <p className="mb-2 pt-mono text-[11px] tabular-nums text-pt-text-soft">
            At {composeAnchor.tc.toFixed(2)}s · ({(composeAnchor.x * 100).toFixed(0)}%,
            {(composeAnchor.y * 100).toFixed(0)}%)
          </p>
        )}
        <Textarea
          value={composeBody}
          onChange={(e) => setComposeBody(e.currentTarget.value)}
          placeholder="What did you notice?"
          rows={4}
          autoFocus
        />
      </Modal>

      {/* Thread modal */}
      <Modal
        open={threadOpen !== null}
        onClose={() => setThreadOpen(null)}
        title={threadOpen ? `Comment from ${threadOpen.author_name}` : ''}
        size="md"
        footer={
          threadOpen ? (
            <div className="flex justify-between gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  void resolve.mutateAsync({ commentId: threadOpen.comment_id })
                  setThreadOpen(null)
                }}
              >
                Mark resolved
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setThreadOpen(null)}>
                  Close
                </Button>
                <Button
                  onClick={handleReply}
                  disabled={reply.isPending || replyBody.trim().length === 0}
                  loading={reply.isPending}
                >
                  Reply
                </Button>
              </div>
            </div>
          ) : null
        }
      >
        {threadOpen && (
          <div className="space-y-3">
            <div className="rounded-sm border border-pt-border bg-pt-panel2 p-3 text-[13px]">
              <div className="mb-1 pt-mono text-[10px] uppercase tracking-wide text-pt-text-soft">
                {threadOpen.author_name} · {new Date(threadOpen.created_at).toLocaleString()}
              </div>
              <div className="text-pt-text">{threadOpen.body}</div>
            </div>
            {threadOpen.replies.map((r) => (
              <div
                key={r.reply_id}
                className="ml-4 rounded-sm border border-pt-border bg-pt-panel2 p-3 text-[13px]"
              >
                <div className="mb-1 pt-mono text-[10px] uppercase tracking-wide text-pt-text-soft">
                  {r.author_name} · {new Date(r.created_at).toLocaleString()}
                </div>
                <div className="text-pt-text">{r.body}</div>
              </div>
            ))}
            <Textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.currentTarget.value)}
              placeholder="Reply…"
              rows={3}
            />
          </div>
        )}
      </Modal>
    </>
  )
}
